import { createContext, useContext } from 'react';
import type { SupportedLocale } from '@/app/config/app.config';
import { en, type MessageKey, type Messages } from './locales/en';
import { ar } from './locales/ar';

const catalogs: Record<SupportedLocale, Messages> = { en, ar };

/**
 * Translate a key for a given locale.
 *
 * - Falls back to EN when the locale's value is empty or missing.
 * - In dev: returns the key when both lookups fail (helps spot missing keys).
 * - In prod: returns empty string.
 */
export function translate(locale: SupportedLocale, key: MessageKey): string {
  const localized = catalogs[locale]?.[key];
  if (localized && localized.length > 0) {
    return localized;
  }
  const fallback = en[key];
  if (fallback && fallback.length > 0) {
    return fallback;
  }
  return import.meta.env.DEV ? key : '';
}

export interface I18nContextValue {
  locale: SupportedLocale;
  setLocale: (locale: SupportedLocale) => void;
  t: (key: MessageKey) => string;
}

export const I18nContext = createContext<I18nContextValue | null>(null);

/**
 * Read the current locale + translator. Must be used under `<I18nProvider>`.
 */
export function useT(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (ctx === null) {
    throw new Error('useT() must be used within <I18nProvider>');
  }
  return ctx;
}

export type { MessageKey, Messages };
export { en, ar };
